import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "../../../components/forms/Buttons/Button";
import CustomDialog from "../../../components/modals/CustomDialog/CustomDialog";
import { deleteQuestionByUUID } from "../../../services/reviewService";
import { getApiErrorCode, getCodedErrorMessage } from "../../../utils/apiError";
import { UserContext } from "../../../utils/contexts/UserContext";
import { BookletLinks, bookletLockMessage, linksBooklets, lockingBooklets, questionBooklets } from "./bookletLock";

// Refusals that find the page out of date: the question moved on meanwhile (403 edit_locked: a reviewer took it,
// so its author may no longer delete it; 409 question_in_use: someone else has had it) or the user's rights
// changed (403 permission_denied).
const STALE_PAGE = ["edit_locked", "question_in_use", "permission_denied"];

// Whether the question's page offers its delete: the detail's actions.delete, and no active BTS booklet prints it.
export const canDeleteQuestion = (question) => !!question?.actions?.delete && !questionBooklets(question).length;

/**
 * The question's delete, after a confirmation: the page opens it (`open`; the "⋯" menu of the question's top bar)
 * and `onClose` closes it. A refusal that finds the page out of date closes the dialog, says why in a toast and has
 * the page read the question again (`onReload`), so its status and its buttons are those of now; a question already
 * gone leaves the page as a delete does (`onDeleted`). An active BTS booklet that prints the question (409
 * question_in_booklet) is said with the way out, and the delete is not offered again: closing the dialog has the page
 * read the question (`onReload`), whose lock notice then comes up front with nothing that offers a change.
 * Anything else stays in the dialog to try again (409 question_in_running_exam, a lost connection).
 */
export default function DeleteQuestionDialog({ question, open, onClose, onDeleted, onReload }) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext) || {};
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  // Refused for a booklet: only Close is left, and it takes the focus (the pressed button lost it while loading).
  const [blocked, setBlocked] = useState(false);
  const actionsRef = useRef(null);

  // Each opening starts afresh (before it is painted).
  useLayoutEffect(() => {
    if (open) {
      setError(null);
      setBlocked(false);
    }
  }, [open]);

  useEffect(() => {
    if (blocked && !deleting) actionsRef.current?.querySelector("[data-close]")?.focus();
  }, [blocked, deleting]);

  const close = () => {
    if (deleting) return;
    onClose();
    // Refused for a booklet: the page is out of date (a booklet was activated meanwhile).
    if (blocked) onReload?.();
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteQuestionByUUID(question.uuid);
      toast.success(t("my_questions.delete.success"));
      onClose();
      onDeleted();
    } catch (err) {
      const code = getApiErrorCode(err);
      const failed = t("my_questions.delete.failed");
      if (err?.response?.status === 404) {
        toast.error(t("question_detail.review.not_found"));
        onClose();
        onDeleted();
      } else if (STALE_PAGE.includes(code)) {
        // edit_locked's own text is about editing: this one says who deletes the question now.
        toast.error(code === "edit_locked" ? t("question_detail.review.delete_locked") : getCodedErrorMessage(t, err, failed));
        onClose();
        onReload?.();
      } else if (code === "question_in_booklet") {
        // The shared text says "change"; this one names the delete, then the way out (an admin's links name the
        // booklets).
        const booklets = lockingBooklets(err);
        const linked = linksBooklets(user, booklets);
        setError({
          message: bookletLockMessage(
            t,
            linked ? "question_detail.review.delete_in_booklet_linked" : "question_detail.review.delete_in_booklet",
            booklets,
            linked,
            { counted: true }
          ),
          booklets: linked ? booklets : [],
        });
        setBlocked(true);
      } else {
        setError({ message: getCodedErrorMessage(t, err, failed) });
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <CustomDialog isOpen={open} closeModal={close} headerText={t("question_detail.review.delete_title")} maxWidth="max-w-md">
      <p className="mt-2 text-sm text-slate-600">{t("my_questions.delete.confirm")}</p>
      {question.code && question.status?.code !== "DRAFT" && (
        <p className="mt-1 text-sm font-medium text-slate-900">{t("question_detail.question_id", { code: question.code })}</p>
      )}
      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
          {error.booklets && <BookletLinks booklets={error.booklets} />}
        </p>
      )}
      <div ref={actionsRef} className="mt-6 flex flex-wrap justify-end gap-2">
        <Button
          data-close
          type="button"
          color="secondary-outline"
          text={blocked ? t("buttons.close") : t("buttons.cancel")}
          onClick={close}
          disabled={deleting}
          className="max-md:h-11"
        />
        <Button
          type="button"
          color="error-solid"
          text={t("buttons.delete")}
          onClick={handleDelete}
          loading={deleting}
          disabled={blocked}
          className="max-md:h-11"
        />
      </div>
    </CustomDialog>
  );
}
